import styled from 'styled-components';
import { Button, InputSearch } from '../Ant';
import { stableScrollGutter } from '../../assets/styles/components/scrollGutter';

const ROW_GAP = '10px';
const PANEL_PADDING = '10px';

export const Trigger = styled(Button)`
  .select-chevron {
    display: none;
    transition: transform 0.2s ease-in-out;
  }
  &:not(:disabled):hover .select-chevron,
  &:focus-visible .select-chevron {
    display: inline-block;
  }
  /* The doubled class keeps the open state above antd's default button background. */
  &&[aria-expanded='true'] {
    background-color: var(--clr-general-hover);
    border-color: var(--clr-general-border-hover);
    .select-chevron {
      display: inline-block;
      transform: rotate(180deg);
    }
  }
`;

export const TriggerIcons = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 15px;
  font-size: 18px;
  color: var(--clr-general-description);
  [data-search-icon] {
    display: inline-flex;
  }
`;

export const Panel = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  /* antd only gives the popup a min-width of the trigger; without this a long label widens it past the field. */
  contain: inline-size;
  margin-top: 4px;
  /* No bottom padding: rows scroll right to the edge, and each child carries its own bottom space. */
  padding: ${PANEL_PADDING} ${PANEL_PADDING} 0;
  border-radius: 5px;
  background-color: var(--clr-container-bg);
  box-shadow: 0px 0px 30px 0px var(--clr-shadow);
  /* Focus lands here first and is handed straight to the search field or the list. */
  outline: none;
`;

/* The transparent border is held back for focus, so the field doesn't grow when it gets one. */
export const SearchField = styled(InputSearch)`
  /* The field is focused the moment the panel opens, so the border waits for a typed term. */
  .ant-input-affix-wrapper-focused:has(.ant-input:not(:placeholder-shown)) {
    border-color: var(--ant-input-active-border-color);
    box-shadow: var(--ant-input-active-shadow);
  }
`;

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${ROW_GAP};
  margin-top: ${ROW_GAP};
`;

export const AppliedFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${ROW_GAP};
  min-width: 0;
`;

const VISIBLE_ROWS = 5;
export const SINGLE_LINE_ROW_HEIGHT = 38;
export const TWO_LINE_ROW_HEIGHT = 58;

/* The visible rows, the gaps between them and the bottom padding, so rows with a description still show five. */
const listViewportHeight = (rowHeight: number) =>
  `calc(${VISIBLE_ROWS} * ${rowHeight}px + ${VISIBLE_ROWS - 1} * ${ROW_GAP} + ${PANEL_PADDING})`;

export const OptionList = styled.div<{ $rowHeight: number }>`
  display: flex;
  flex-direction: column;
  gap: ${ROW_GAP};
  max-height: ${({ $rowHeight }) => listViewportHeight($rowHeight)};
  padding-bottom: ${PANEL_PADDING};
  overflow-y: auto;
  ${stableScrollGutter}
  &:not(:first-child) {
    margin-top: ${ROW_GAP};
  }
  &:empty {
    padding-bottom: 0;
  }
  &:focus-visible {
    outline: 2px solid var(--clr-general-primary);
    outline-offset: -2px;
    border-radius: 5px;
  }
`;

/* Truncated on one line rather than line-clamped: a clamp wraps at word boundaries and leaves a gap before the ellipsis. */
export const OptionLabel = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const OptionDescription = styled.span`
  font-weight: 400;
  color: var(--clr-subtitle);
`;

export const OptionRow = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 40px;
  padding: 10px;
  /* Transparent until selected, so picking a row recolours the border without growing it. */
  border: 1px solid transparent;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  /* Lets an unbroken word wrap, so the line clamp can end it with an ellipsis instead of clipping it. */
  overflow-wrap: anywhere;
  color: var(--clr-title);
  cursor: pointer;
  &[data-active='true'] {
    background-color: var(--clr-general-hover);
    color: var(--clr-general-primary);
  }
  &[aria-selected='true'] {
    border-color: var(--clr-general-primary);
    background-color: var(--selected);
    color: var(--clr-title);
    &[data-active='true'] {
      background-color: var(--selected-hover);
    }
  }
  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SkeletonRow = styled(OptionRow)`
  cursor: default;
`;

/* Sits inside the scrolling list, so it extends the scroll range instead of the panel's height. */
export const LoadingMore = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: center;
`;

/* Fills the space a full list would scroll in, so an empty result doesn't shrink the panel. */
export const EmptyState = styled.div<{ $rowHeight: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ $rowHeight }) => listViewportHeight($rowHeight)};
`;
